import type React from "react";

import { AeoCampaignModal } from "app/components/AeoCampaignModal/AeoCampaignModal";
import { AEO_CAMPAIGN_ID } from "app/components/AeoCampaignModal/configs/buildAeoDiscountCampaignConfig";
import { AnnualPromoCampaignModal } from "app/components/AnnualPromoModal/AnnualPromoCampaignModal";
import { ANNUAL_CAMPAIGN_ID } from "app/components/AnnualPromoModal/configs/buildAnnualCampaignConfig";
import { FIRST_WEEK_CAMPAIGN_ID } from "app/components/FirstWeekMonthlyPromo/configs/buildFirstWeekDiscountCampaignConfig";
import { FirstWeekCampaignModal } from "app/components/FirstWeekMonthlyPromo/FirstWeekCampaignModal";
import { useDiscounts } from "app/components/hooks/useDiscounts";
import type { PageTopBannerType } from "app/components/PageTopBanner/PageTopBannerType";
import { PageTopBannerDict } from "app/components/PageTopBanner/PageTopBannerType";
import { usePageTopBanner } from "app/components/PageTopBanner/usePageTopBanner";
import { SeasonalCampaignModal } from "app/marketing/components/seasonal-campaign/seasonal-campaign-modal/SeasonalCampaignModal";

import { DunningsModals } from "../dunnings-modals/DunningsModals";

const modalComponentMap: Partial<Record<PageTopBannerType, React.ReactNode>> = {
    [PageTopBannerDict.TRIAL_BANNER]: null,
    [PageTopBannerDict.SEASONAL_CAMPAIGN_BANNER]: <SeasonalCampaignModal />,
    [PageTopBannerDict.DUNNINGS]: <DunningsModals />,
    [PageTopBannerDict.PROJECTS_PAGE_CTA]: null,
    [PageTopBannerDict.ADDON_CART]: null,
};

/**
 * Maps a registered discount campaign id (see `buildRegisteredDiscountCampaignConfigs`) to its modal.
 * Add a new entry whenever a campaign is registered in the registry so MarketingModals
 * can render the right modal when that campaign wins.
 */
const discountModalByCampaignId: Record<string, React.ReactNode> = {
    [FIRST_WEEK_CAMPAIGN_ID]: <FirstWeekCampaignModal />,
    [ANNUAL_CAMPAIGN_ID]: <AnnualPromoCampaignModal />,
    [AEO_CAMPAIGN_ID]: <AeoCampaignModal />,
};

export const MarketingModals = () => {
    /**
     * NOTE: We're using the banner hook to decide which one to show, because the modals are
     * a subset of the banners
     */
    const { pageTopBannerType } = usePageTopBanner();
    const { activeCampaignId } = useDiscounts();

    if (pageTopBannerType === PageTopBannerDict.DISCOUNT_BANNER) {
        return (activeCampaignId && discountModalByCampaignId[activeCampaignId]) ?? null;
    }

    /**
     * NOTE: Make sure to have here ALL types, even if they need no modal, so that
     * we'll always have the same banner and modal type in the end
     */
    const modalComponent = modalComponentMap[pageTopBannerType];
    return modalComponent ?? null;
};
